/**
 * API e stato server della wishlist (SPEC-012.2 §3.1).
 *
 * ⚠️ Importi e percentuali sono STRINGHE decimali (ADR-0006): si formattano con
 * `lib/format.ts` e non si convertono in `number` per fare calcoli.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type WishlistItem = {
  id: string;
  instrument: {
    id: string;
    name: string;
    /** Nome pubblico (F-087). */
    commercialName: string | null;
    symbol: string | null;
    isin: string | null;
    instrumentType: string;
    currency: string;
    /** Mappatura verso il ticker del broker (SPEC-014 R1): null = non associato. */
    trading212Ticker: string | null;
  };
  portfolio: { id: string; name: string; baseCurrency: string };
  maxExposureAmount: string | null;
  /** Valuta del PORTAFOGLIO: arriva dal backend, la UI non la deduce. */
  maxExposureCurrency: string;
  maxPortfolioPercentage: string | null;
  /** Appunti privati: non entrano in nessun prompt. */
  internalNotes: string | null;
  /** Istruzioni che raggiungono il modello nella verifica di auto-trading. */
  aiNotes: string | null;
  expiresAt: string;
  /** Negativo se già scaduta. */
  daysRemaining: number;
  expired: boolean;
  /** La riga sopravvive alla rimozione dalla watchlist: qui per il badge. */
  stillInWatchlist: boolean;
  /** Tag dell'utente su quel titolo (F-079), mostrati in piccolo. */
  tags: string[];
  createdAt: string;
  updatedAt: string;
};

export type WishlistResponse = {
  items: WishlistItem[];
  total: number;
  /** Finestra di default lato server: la UI la mostra invece di duplicarla. */
  defaultExpiryDays: number;
};

export type CreateWishlistInput = {
  instrumentId: string;
  portfolioId: string;
  maxExposureAmount?: string | null;
  maxPortfolioPercentage?: string | null;
  internalNotes?: string | null;
  aiNotes?: string | null;
  expiresAt?: string;
};

export type UpdateWishlistInput = {
  id: string;
  maxExposureAmount?: string | null;
  maxPortfolioPercentage?: string | null;
  internalNotes?: string | null;
  aiNotes?: string | null;
  expiresAt?: string;
  renew?: true;
};

export const wishlistKeys = {
  all: ['wishlist'] as const,
  scoped: (portfolioId: string | null, includeExpired: boolean) =>
    ['wishlist', portfolioId ?? 'all', includeExpired] as const,
};

function buildQuery(portfolioId: string | null, includeExpired: boolean): string {
  const params = new URLSearchParams();
  if (portfolioId) params.set('portfolioId', portfolioId);
  if (includeExpired) params.set('includeExpired', 'true');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useWishlist(portfolioId: string | null = null, includeExpired = false) {
  return useQuery({
    queryKey: wishlistKeys.scoped(portfolioId, includeExpired),
    queryFn: ({ signal }) =>
      api.get<WishlistResponse>(`/wishlist${buildQuery(portfolioId, includeExpired)}`, signal),
  });
}

export function useCreateWishlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWishlistInput) => api.post<WishlistItem>('/wishlist', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['instruments', 'detail'] });
    },
  });
}

export function useUpdateWishlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateWishlistInput) => api.patch<WishlistItem>(`/wishlist/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: wishlistKeys.all }),
  });
}

export function useDeleteWishlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/wishlist/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: wishlistKeys.all }),
  });
}
